import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import themes, { defaultThemeName } from '../app/Theme';

const THEME_STORAGE_KEY = 'ndh_theme_v3';
const LEGACY_THEME_KEYS = ['ndh_theme', 'ndh_theme_v2'];

const ThemeContext = createContext({
    themeName: defaultThemeName,
    setThemeName: () => {},
    toggleTheme: () => {},
    availableThemes: Object.keys(themes),
});

export const ThemeContextProvider = ({ children }) => {
    const [themeName, setThemeName] = useState(() => {
        if (typeof window !== 'undefined') {
            const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
            if (stored && themes[stored]) {
                return stored;
            }
        }
        return defaultThemeName;
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(THEME_STORAGE_KEY, themeName);
            LEGACY_THEME_KEYS.forEach((key) => {
                if (key !== THEME_STORAGE_KEY) {
                    window.localStorage.removeItem(key);
                }
            });
        }
    }, [themeName]);

    const toggleTheme = useCallback(() => {
        setThemeName((prev) => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    const contextValue = useMemo(() => ({
        themeName,
        setThemeName,
        toggleTheme,
        availableThemes: Object.keys(themes),
    }), [themeName, toggleTheme]);

    const activeTheme = themes[themeName] || themes[defaultThemeName];

    return (
        <ThemeContext.Provider value={contextValue}>
            <ThemeProvider theme={activeTheme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeContext = () => useContext(ThemeContext);

export default ThemeContext;
